import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { fbt } from 'fbtee'
import type { ClassifiedError } from '@/lib/errors'
import { pageTitle, useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useGuestGuard } from '@/hooks/useGuestGuard'
import { PageLayout } from '@/components/PageLayout'
import { FeedbackForm } from '@/components/FeedbackForm'
import { Button } from '@/components/ui/button'

export default function SupportPage() {
  useDocumentTitle(pageTitle(fbt('Help & Feedback', 'Browser tab title for the support page')))
  const location = useLocation()
  const { guard } = useGuestGuard()
  const initialErrorCtx = (location.state as { error?: ClassifiedError } | null)?.error ?? null

  const [errorCtx, setErrorCtx] = useState<ClassifiedError | null>(initialErrorCtx)
  const [sent, setSent] = useState(false)
  const [formKey, setFormKey] = useState(0)

  function sendAnother() {
    setErrorCtx(null)
    setFormKey(k => k + 1)
    setSent(false)
  }

  if (sent) {
    return (
      <PageLayout title={fbt('Help & Feedback', 'Page title for the support page')} back>
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center space-y-3">
          <p className="text-lg font-medium">
            {fbt('Thanks for reaching out!', 'Success heading after submitting support form')}
          </p>
          <p className="text-sm text-muted-foreground">
            {fbt(
              "We'll get back to you as soon as we can.",
              'Success description after submitting support form',
            )}
          </p>
          <Button variant="outline" className="mt-2" onClick={sendAnother}>
            {fbt(
              'Send more feedback',
              'Button on the support success screen that reopens an empty feedback form',
            )}
          </Button>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title={fbt('Help & Feedback', 'Page title for the support page')} back>
      <div className="px-4 py-4 max-w-lg mx-auto">
        <p className="text-sm text-muted-foreground mb-4">
          {fbt(
            'Bug reports, feature ideas, questions — straight to the dev.',
            'Description on the support page explaining what kind of messages are welcome',
          )}
        </p>
        <FeedbackForm
          key={formKey}
          errorContext={errorCtx}
          onSuccess={() => setSent(true)}
          wrapSubmit={fn => guard(fn, { intent: 'support', title: fbt('Sign in to send', 'Guest guard title for support form') })}
          fullWidthSubmit
        />
      </div>
    </PageLayout>
  )
}
